import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { throttle } from 'es-toolkit';
import type { Rect } from 'react-use-rect';
import { defaultRect } from './use-stable-rect';

type ObserverOptions = {
  withBorderBoxSizes?: boolean;
  throttleMs?: number;
};

type RevalidateFn = () => void;

export const useResizeObserver = (
  element: HTMLElement | null | undefined,
  options?: ObserverOptions
): [Rect, RevalidateFn] => {
  const [observerEntry, setObserverEntry] =
    useState<ResizeObserverEntry | null>(null);
  const observer = useRef<ResizeObserver | null>(null);

  const { throttleMs, withBorderBoxSizes } = options ?? {};

  const disconnect = useCallback(() => observer.current?.disconnect(), []);

  const observe = useCallback(() => {
    const resizeObserverCallback: ResizeObserverCallback = ([entry]) => {
      setObserverEntry(entry);
    };
    const throttleRevalidate = throttle(
      resizeObserverCallback,
      throttleMs ?? 0
    );

    observer.current = new ResizeObserver(throttleRevalidate);

    if (element) {
      observer.current.observe(element);
    }
  }, [element, throttleMs]);

  const revalidate = useCallback<RevalidateFn>(() => {
    setObserverEntry(null);
    disconnect();
    observe();
  }, [disconnect, observe]);

  useLayoutEffect(() => {
    observe();

    return disconnect;
  }, [disconnect, observe]);

  // Такое преобразование необходимо из-за того, что contentRect является DOMReadOnly
  const plainContentRect = toPlainRect(
    observerEntry?.contentRect ?? defaultRect
  );

  const borderBox = observerEntry?.borderBoxSize?.[0];

  const rect = {
    ...plainContentRect,
    ...(withBorderBoxSizes && borderBox
      ? {
          width: borderBox.inlineSize,
          height: borderBox.blockSize
        }
      : {})
  };

  return [rect, revalidate];
};

const toPlainRect = (rect: DOMRectReadOnly | Rect): Rect => ({
  x: rect.x,
  y: rect.y,
  top: rect.top,
  left: rect.left,
  bottom: rect.bottom,
  right: rect.right,
  width: rect.width,
  height: rect.height
});
